import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AISummaryPanel } from '@/components/shared/AISummaryPanel';
import { Heart, Brain, Target, AlertCircle, Users, Globe, Sparkles, Phone, Mail, MapPin, Pencil } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { ParticipantOverviewEditDrawer } from './ParticipantOverviewEditDrawer';

function SectionCard({ icon: Icon, title, children, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2.5">
          <Icon className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function BulletList({ items, max = 5, emptyText = 'None recorded' }) {
  if (!items || items.length === 0) {
    return <p className="text-xs text-muted-foreground italic">{emptyText}</p>;
  }
  const visible = items.slice(0, max);
  const overflow = items.length - max;
  return (
    <div className="space-y-1">
      {visible.map((item, i) => (
        <div key={i} className="text-sm flex items-start gap-2">
          <span className="text-muted-foreground mt-1.5 w-1 h-1 rounded-full bg-muted-foreground flex-shrink-0" />
          <span className="line-clamp-2">{item}</span>
        </div>
      ))}
      {overflow > 0 && <p className="text-xs text-muted-foreground">+{overflow} more</p>}
    </div>
  );
}

export function ParticipantOverviewV2({ participant, participantId, accessControl, dashboard, editEnabled, onRefresh }) {
  const { user } = useAuth();
  const canEdit = editEnabled && (user?.permissions || []).includes('participants.manage');
  const p = participant;

  const [overviewProfile, setOverviewProfile] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const fetchProfile = useCallback(() => {
    api.get(`/participants/${participantId}/overview-profile`)
      .then(r => setOverviewProfile(r.data.data || {}))
      .catch(() => setOverviewProfile(null));
  }, [participantId]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleSaved = () => {
    setEditOpen(false);
    fetchProfile();
    if (onRefresh) onRefresh();
  };

  // Merge: prefer overview_profile stored data, then fall back to participant fields
  const op = overviewProfile || {};
  const hasStoredProfile = op && Object.keys(op).length > 0 && (op.goals_aspirations || op.allergies || op.family_support_network || op.primary_disability);

  // Disability
  const disabilityCondition = op.primary_disability || p.primary_disability || p.disability_type || null;
  const disabilityImpacts = op.disability_impact || [];

  // Goals
  const goalsList = op.goals_aspirations || [];

  // Allergies
  const allergiesList = op.allergies || [];

  // Family
  const familyEntries = op.family_support_network || [];
  // Fallback to participant fields if no stored profile
  const familyDisplay = familyEntries.length > 0 ? familyEntries : (() => {
    const fb = [];
    if (p.guardian_name) fb.push({ name: p.guardian_name, relationship: 'Guardian', phone: p.guardian_phone || '' });
    if (p.emergency_contact_name) fb.push({ name: p.emergency_contact_name, relationship: 'Emergency Contact', phone: p.emergency_contact_phone || '' });
    return fb;
  })();

  // Cultural
  const cultural = op.cultural_linguistic_needs || {};
  const hasCultural = cultural.language || cultural.notes || cultural.interpreter_required || p.primary_language || p.cultural_notes;
  const displayLang = cultural.language || p.primary_language || '';
  const displayInterpreter = cultural.interpreter_required || p.interpreter_required;
  const displayCulturalNotes = cultural.notes || p.cultural_notes || '';

  // Hobbies
  const hobbiesList = op.hobbies_interests || [];

  // Address
  const addressParts = [p.address, p.suburb, p.state_id, p.postcode].filter(Boolean);
  const addressText = addressParts.join(', ');

  return (
    <div className="space-y-4" data-testid="participant-overview-v2">
      {/* Dashboard KPI Cards */}
      {dashboard && (
        <div className={`grid grid-cols-2 ${accessControl.show_funding_amount ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-3`} data-testid="participant-dashboard">
          {accessControl.show_funding_amount && (
            <Card><CardContent className="py-3 text-center"><p className="text-xl font-bold text-green-600">${dashboard.funding_remaining?.toLocaleString()}</p><p className="text-[10px] text-muted-foreground">Funding Remaining</p></CardContent></Card>
          )}
          <Card><CardContent className="py-3 text-center"><p className="text-xl font-bold">{dashboard.active_shifts}</p><p className="text-[10px] text-muted-foreground">Active Shifts</p></CardContent></Card>
          <Card><CardContent className="py-3 text-center"><p className="text-xl font-bold">{dashboard.assigned_staff_count}</p><p className="text-[10px] text-muted-foreground">Assigned Staff</p></CardContent></Card>
          <Card className={dashboard.high_critical_risks > 0 ? 'border-red-200' : ''}><CardContent className="py-3 text-center"><p className={`text-xl font-bold ${dashboard.high_critical_risks > 0 ? 'text-red-600' : ''}`}>{dashboard.active_risks}</p><p className="text-[10px] text-muted-foreground">Active Risks</p></CardContent></Card>
          <Card><CardContent className="py-3 text-center"><p className="text-xl font-bold">{dashboard.upcoming_appointments}</p><p className="text-[10px] text-muted-foreground">Upcoming Appts</p></CardContent></Card>
        </div>
      )}

      {/* Edit Button */}
      {canEdit && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setEditOpen(true)} data-testid="overview-edit-btn">
            <Pencil className="w-3 h-3 mr-1" /> Edit Overview
          </Button>
        </div>
      )}

      {/* Structured Overview Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Contact & Personal */}
        <SectionCard icon={Phone} title="Contact Details" testId="overview-contact">
          <div className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-2">
              {p.gender && <div><span className="text-muted-foreground text-xs">Gender</span><br/><span className="font-medium">{p.gender}{p.pronouns && <span className="text-muted-foreground ml-1">({p.pronouns})</span>}</span></div>}
              {p.phone && <div><span className="text-muted-foreground text-xs">Phone</span><br/><span className="font-medium">{p.phone}</span></div>}
              {p.email && <div><span className="text-muted-foreground text-xs">Email</span><br/><span className="font-medium truncate block max-w-[200px]">{p.email}</span></div>}
              {p.alternate_phone && <div><span className="text-muted-foreground text-xs">Alt Phone</span><br/><span className="font-medium">{p.alternate_phone}</span></div>}
            </div>
            {addressText && (
              <div className="flex items-start gap-1.5 pt-1">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <span className="text-sm">{addressText}</span>
              </div>
            )}
            {accessControl.can_view_funding && (
              <div className="pt-1 flex gap-2 flex-wrap">
                <Badge variant="outline" className="text-[10px]">{p.funding_type ? p.funding_type.replace(/_/g, ' ') : 'Funding Not Set'}</Badge>
                {p.plan_start_date && <span className="text-xs text-muted-foreground">{p.plan_start_date} — {p.plan_end_date || 'Ongoing'}</span>}
              </div>
            )}
            {!p.gender && !p.phone && !p.email && !addressText && <p className="text-xs text-muted-foreground italic">No contact details recorded</p>}
          </div>
        </SectionCard>

        {/* Disability Details */}
        <SectionCard icon={Brain} title="Disability Details" testId="overview-disability">
          {disabilityCondition ? (
            <div className="space-y-2">
              <div><span className="text-xs text-muted-foreground">Primary Condition</span><p className="text-sm font-medium">{disabilityCondition}</p></div>
              {disabilityImpacts.length > 0 && (
                <div><span className="text-xs text-muted-foreground">Impact / Needs</span><BulletList items={disabilityImpacts} /></div>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">No disability details recorded</p>
          )}
        </SectionCard>

        {/* Goals & Aspirations */}
        <SectionCard icon={Target} title="Goals & Aspirations" testId="overview-goals">
          <BulletList items={goalsList} emptyText="No goals or aspirations recorded" />
        </SectionCard>

        {/* Allergies */}
        <SectionCard icon={AlertCircle} title="Allergies" testId="overview-allergies">
          {allergiesList.length > 0 ? (
            <div className="flex gap-1.5 flex-wrap">
              {allergiesList.map((a, i) => <Badge key={i} variant="outline" className="text-xs bg-red-50/50 text-red-700 border-red-200">{a}</Badge>)}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">No known allergies</p>
          )}
        </SectionCard>

        {/* Family & Support Network */}
        <SectionCard icon={Users} title="Family & Support Network" testId="overview-family">
          {familyDisplay.length > 0 ? (
            <div className="space-y-2">
              {familyDisplay.map((f, i) => (
                <div key={i} className="text-sm">
                  <span className="font-medium">{f.name}</span>
                  {f.relationship && <Badge variant="secondary" className="text-[9px] ml-1.5">{f.relationship}</Badge>}
                  {f.phone && <span className="text-xs text-muted-foreground ml-2">{f.phone}</span>}
                  {f.notes && <p className="text-xs text-muted-foreground mt-0.5">{f.notes}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">No family or support network recorded</p>
          )}
        </SectionCard>

        {/* Cultural & Linguistic */}
        <SectionCard icon={Globe} title="Cultural & Linguistic Needs" testId="overview-cultural">
          {hasCultural ? (
            <div className="space-y-1.5 text-sm">
              {displayLang && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-xs">Language:</span>
                  <span className="font-medium">{displayLang}</span>
                  {displayInterpreter && <Badge variant="destructive" className="text-[9px]">Interpreter Required</Badge>}
                </div>
              )}
              {displayCulturalNotes && <p className="text-xs line-clamp-3">{displayCulturalNotes}</p>}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">No cultural or linguistic needs recorded</p>
          )}
        </SectionCard>

        {/* Hobbies & Interests */}
        {hobbiesList.length > 0 && (
          <SectionCard icon={Sparkles} title="Hobbies & Interests" testId="overview-hobbies">
            <div className="flex gap-1.5 flex-wrap">
              {hobbiesList.map((h, i) => <Badge key={i} variant="outline" className="text-xs">{h}</Badge>)}
            </div>
          </SectionCard>
        )}
      </div>

      {/* AI Summary */}
      <AISummaryPanel summaryType="PARTICIPANT" entityId={participantId} entityType="PARTICIPANT" title="AI Summary" />

      {/* Edit Drawer */}
      {editOpen && (
        <ParticipantOverviewEditDrawer
          participantId={participantId}
          currentProfile={overviewProfile || {}}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
